import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { authApiClient, ApiError } from "@/api/authApiClient";
import { AlertsWrapper } from "@/components/AlertsWrapper";
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ROUTES } from "@/routes/paths";
import { Eye, EyeOff } from "lucide-react";

export const Login = () => {
  const [errors, setErrors] = useState<ApiError[]>([]);
  const [messages, setMessages] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [currentEmail, setCurrentEmail] = useState("");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const messageParam = searchParams.get("message");
    if (messageParam) {
      setMessages([messageParam]);
      navigate(window.location.pathname, { replace: true });
    }

    const errorParam = searchParams.get("error");
    if (errorParam) {
      setErrors([errorParam]);
      navigate(window.location.pathname, { replace: true });
    }
  }, [searchParams, navigate]);

  const handleEmailValidation = async (email: string) => {
    setCurrentEmail(email);
    const { success, errors, data } = await authApiClient.validateEmail(email);
    if (success) {
      if (data?.sso) {
        window.location.href = data.sso;
      } else {
        setShowPassword(true);
        setRequiresTwoFactor(data?.two_factor_authentication_required || false);
      }
    }
    setErrors(errors);
  };

  const handleLogin = async (email: string, password: string) => {
    const { success, errors } = await authApiClient.login(
      email,
      password,
      requiresTwoFactor,
    );
    if (success) {
      if (requiresTwoFactor) {
        localStorage.setItem("otpEmail", email);
        navigate(`/${ROUTES.PUBLIC.OTP_VERIFICATION}`);
      } else {
        navigate("/");
      }
    } else {
      setErrors(errors);
    }
  };

  return (
    <main role="main">
      <h2 className="mb-3 text-2xl font-medium text-foreground">
        {t("login.signInHeading")}
      </h2>
      <p className="mb-6 text-sm text-muted-foreground">
        {t("login.subtitle")}
      </p>
      <AlertsWrapper errors={errors} messages={messages}>
        <LoginForm
          handleEmailValidation={handleEmailValidation}
          handleLogin={handleLogin}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          currentEmail={currentEmail}
        />
      </AlertsWrapper>
    </main>
  );
};

interface LoginFields {
  email: string;
  password: string;
}

function LoginForm({
  handleLogin,
  handleEmailValidation,
  showPassword,
  currentEmail,
}: {
  handleLogin: (email: string, password: string) => Promise<void>;
  handleEmailValidation: (email: string) => Promise<void>;
  showPassword: boolean;
  setShowPassword: (show: boolean) => void;
  currentEmail: string;
}) {
  const [hidePassword, setHidePassword] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const {
    register,
    handleSubmit,
    trigger,
    formState: { isValid, isSubmitting },
  } = useForm<LoginFields>({ disabled });
  const { t } = useTranslation();

  useEffect(() => {
    trigger();
  }, [showPassword, trigger]);

  const onSubmit: SubmitHandler<LoginFields> = async (fields: LoginFields) => {
    const { email, password } = fields;
    setDisabled(true);
    if (showPassword) await handleLogin(email, password);
    else await handleEmailValidation(email);
    setDisabled(false);
  };

  const passwordEyeTitle = hidePassword
    ? t("login.showPassword")
    : t("login.hidePassword");

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-foreground">
            {t("login.emailLabel")}
          </label>
          <input
            {...register("email", { required: true })}
            type="email"
            placeholder={t("login.email")}
            data-testid="email-input"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium text-muted-foreground focus:ring-2 focus:ring-ring focus:outline-none"
          />
        </div>
        {showPassword && (
          <>
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-foreground">
                {t("login.passwordLabel")}
              </label>
              <div className="relative">
                <input
                  {...register("password", { required: true })}
                  type={hidePassword ? "password" : "text"}
                  placeholder={t("login.password")}
                  data-testid="password-input"
                  className="flex h-10 w-full rounded-md border border-input bg-background py-2 pr-10 pl-3 text-sm font-medium text-muted-foreground focus:ring-2 focus:ring-ring focus:outline-none"
                />
                <div className="absolute top-0 right-0 bottom-0 flex w-10 justify-center">
                  <button
                    type="button"
                    className="text-icon"
                    onClick={() => setHidePassword((prev) => !prev)}
                    title={passwordEyeTitle}
                    aria-label={passwordEyeTitle}
                  >
                    {hidePassword ? <Eye /> : <EyeOff />}
                  </button>
                </div>
              </div>
            </div>
            <div className="mb-6 flex items-center justify-end">
              <Link
                to="/forgot_password"
                state={{ email: currentEmail }}
                className="text-sm font-medium text-muted-foreground underline hover:no-underline"
              >
                {t("login.forgotPassword")}
              </Link>
            </div>
          </>
        )}
        <button
          type="submit"
          disabled={!isValid || isSubmitting}
          className="flex w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed"
        >
          {isSubmitting
            ? t("login.loading")
            : showPassword
              ? t("login.signIn")
              : t("login.next")}
        </button>
      </form>
      <div className="mt-4 text-center">
        <span className="text-sm text-muted-foreground">
          {t("login.noAccount")}
        </span>
        <span className="ml-1">
          <a
            href="mailto:support@zeemee.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-foreground underline hover:no-underline"
          >
            {t("login.contactUs")}
          </a>
        </span>
      </div>
    </div>
  );
}
